/* eslint-disable react/no-danger */
import * as React from 'react';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { formStyles } from 'styles/formStyles';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import {
    artikelGruppenByNrSelector,
    einheitenByCodeSelector,
    lookupsLoadingFinishedSelector,
} from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { FilterValues } from './types';
import { ArtikelumsatzSummaryProps, useArtikelumsatzSummary } from './hooks/useArtikelumsatzSummary';

const theme: ITheme = getTheme();
const { palette } = theme;

const odd = palette.neutralLight;
const even = palette.neutralLighterAlt;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },

    borderTop: {
        borderTop: '1px solid',
    },
    strongBorderTop: {
        borderTop: '3px solid',
    },
    borderBottom: {
        borderBottom: '1px solid',
    },
    strongBorderBottom: {
        borderBottom: '3px solid',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    growth: {
        fontSize: 12,
        lineHeight: 12,
    },
    positiveGrowth: {
        color: palette.green,
    },
    negativeGrowth: {
        color: palette.red,
    },
    auNr: {},
    menge: {
        backgroundColor: odd,
    },
    aufwand: {
        backgroundColor: even,
    },
    umsatz: {
        backgroundColor: odd,
    },
    borderRight: {
        borderRight: '1px solid',
    },
});

interface ArtikelumsatzProps extends FilterValues {
    printView?: boolean;
}

export const Artikelumsatz: React.FC<ArtikelumsatzProps> = ({
    datumVon,
    datumBis,
    artikelGruppeNr,
    artikelNr,
    showCompare,
    printView = false,
}) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    /*
    const artikelDetailKalkulationen = useArtikelDetailKalkulationList({
        artikelGruppeNr: artikelGruppeNr,
        artikelNr: artikelNr,
    });

    if (artikelDetail && artikelDetailKalkulationen) {
        const defaultKalkulation = artikelDetailKalkulationen.find(
          (k) => k.eh === artikelDetail.artikel?.einheit,
        );

        if (defaultKalkulation?.ek2) {
            ekPreis = defaultKalkulation.ek2;
        }
    }
     */

    const artikelumsatzSummaryPropsJ0 = React.useMemo<ArtikelumsatzSummaryProps>(
        () => ({
            datumVon,
            datumBis,
            artikelGruppeNr,
            artikelNr,
            enabled: true,
        }),
        [datumVon, datumBis, artikelGruppeNr, artikelNr],
    );

    const artikelumsatzJ0 = useArtikelumsatzSummary(artikelumsatzSummaryPropsJ0);

    const artikelumsatzSummaryPropsJ1 = React.useMemo<ArtikelumsatzSummaryProps>(
        () => ({
            datumVon: moment(datumVon).subtract({ years: 1 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 1 }).format('YYYY-MM-DD'),
            artikelGruppeNr,
            artikelNr,
            enabled: true,
        }),
        [datumVon, datumBis, artikelGruppeNr, artikelNr],
    );

    const artikelumsatzJ1 = useArtikelumsatzSummary(artikelumsatzSummaryPropsJ1);

    const artikelumsatzSummaryPropsJ2 = React.useMemo<ArtikelumsatzSummaryProps>(
        () => ({
            datumVon: moment(datumVon).subtract({ years: 2 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 2 }).format('YYYY-MM-DD'),
            artikelGruppeNr,
            artikelNr,
            enabled: true,
        }),
        [datumVon, datumBis, artikelGruppeNr, artikelNr],
    );

    const artikelumsatzJ2 = useArtikelumsatzSummary(artikelumsatzSummaryPropsJ2);

    const artikelumsatzSummaryPropsJ3 = React.useMemo<ArtikelumsatzSummaryProps>(
        () => ({
            datumVon: moment(datumVon).subtract({ years: 3 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 3 }).format('YYYY-MM-DD'),
            artikelGruppeNr,
            artikelNr,
            enabled: true,
        }),
        [datumVon, datumBis, artikelGruppeNr, artikelNr],
    );

    const artikelumsatzJ3 = useArtikelumsatzSummary(artikelumsatzSummaryPropsJ3);

    const percentClassNameHead = classnames(
        styles.tdHead,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
    );

    const percentClassNameBody = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
    );

    const moneyClassNameHead = classnames(
        styles.tdHead,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const moneyClassNameBody = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const renderHeaderRow = () => {
        return (
            <tr>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeLimitedCell,
                    )}
                >
                    Kunde
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportArtikelGruppePrintCell : formStyles.reportArtikelGruppeCell,
                    )}
                >
                    Artikelgruppe
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportArtikelNrPrintCell : formStyles.reportArtikelNrCell,
                    )}
                >
                    Artikel-Nr.
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportArtikelTextPrintCell : formStyles.reportArtikelTextCell,
                    )}
                >
                    Artikel
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView
                            ? formStyles.reportReclayProduktgruppePrintCell
                            : formStyles.reportReclayProduktgruppeCell,
                    )}
                >
                    Reclay Produktgruppe
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportEinheitCell : formStyles.reportEinheitCell,
                    )}
                >
                    Einheit
                </td>
                {showCompare && <td className={classnames(percentClassNameHead, styles.menge)} />}
                <td className={classnames(moneyClassNameHead, styles.menge)}>Menge</td>
                {showCompare && <td className={classnames(percentClassNameHead, styles.aufwand)} />}
                <td className={classnames(moneyClassNameHead, styles.aufwand)}>Aufwand</td>
                {showCompare && <td className={classnames(percentClassNameHead, styles.umsatz)} />}
                <td className={classnames(moneyClassNameHead, styles.umsatz)}>Umsatz</td>
                {/* <td className={classnames(moneyClassNameHead)}>BMarge</td> */}
                {/* <td className={classnames(moneyClassNameHead)}>BMarge %</td> */}
                {/* <td className={classnames(moneyClassNameHead)}>Vgl.VJ</td> */}
                {/* <td className={classnames(moneyClassNameHead)}>Vgl.3J</td> */}
            </tr>
        );
    };

    const renderValueWithGrowth = (
        value: number,
        valuePrev: number | undefined,
        valueAvg: number | undefined,
        className: string,
        isMoney: boolean,
        borderRight?: boolean,
    ) => {
        let growthPrev: number | undefined = valuePrev ? (value * 100) / valuePrev - 100 : undefined;
        let colorPrev = '';

        if (growthPrev) {
            if (Math.abs(growthPrev) > 1000) {
                growthPrev = undefined;
            }

            if (growthPrev) {
                if (growthPrev > 0) {
                    colorPrev = styles.positiveGrowth;
                } else if (growthPrev < 0) {
                    colorPrev = styles.negativeGrowth;
                }
            }
        }

        let growthAvg: number | undefined = valueAvg ? (value * 100) / valueAvg - 100 : undefined;
        let colorAvg = '';

        if (growthAvg) {
            if (Math.abs(growthAvg) > 1000) {
                growthAvg = undefined;
            }

            if (growthAvg) {
                if (growthAvg > 0) {
                    colorAvg = styles.positiveGrowth;
                } else if (growthAvg < 0) {
                    colorAvg = styles.negativeGrowth;
                }
            }
        }

        return (
            <>
                {showCompare && (
                    <td className={classnames(percentClassNameBody, className)}>
                        {growthPrev === undefined ? (
                            <span className={styles.growth}>&nbsp;</span>
                        ) : (
                            <span
                                className={classnames(styles.growth, colorPrev)}
                                title={
                                    valuePrev
                                        ? `Vorjahr: ${isMoney ? formatMoney(valuePrev) : formatDecimal(valuePrev)}`
                                        : ''
                                }
                            >
                                {formatPercent(growthPrev, 0)}
                            </span>
                        )}
                        <br />
                        {growthAvg === undefined ? (
                            <span className={styles.growth}>&nbsp;</span>
                        ) : (
                            <span
                                className={classnames(styles.growth, colorAvg)}
                                title={
                                    valueAvg
                                        ? `Durchschnitt 3J: ${
                                              isMoney ? formatMoney(valueAvg) : formatDecimal(valueAvg)
                                          }`
                                        : ''
                                }
                            >
                                {formatPercent(growthAvg, 0)}
                            </span>
                        )}
                    </td>
                )}
                <td
                    className={classnames(moneyClassNameBody, className, {
                        [styles.borderRight]: borderRight,
                    })}
                >
                    {isMoney ? formatMoney(value) : formatDecimal(value)}
                </td>
            </>
        );
    };

    const getValueWithCompares = (kNr: number, column: 'menge' | 'aufwand' | 'umsatz') => {
        const lineJ0 = artikelumsatzJ0.lines.find((line) => line.kNr === kNr);
        const lineJ1 = artikelumsatzJ1.lines.find((line) => line.kNr === kNr);
        const lineJ2 = artikelumsatzJ2.lines.find((line) => line.kNr === kNr);
        const lineJ3 = artikelumsatzJ3.lines.find((line) => line.kNr === kNr);

        const valueJ0 = lineJ0?.[column] || 0;
        const valueJ1 = lineJ1?.[column] || 0;
        const valueJ2 = lineJ2?.[column] || 0;
        const valueJ3 = lineJ3?.[column] || 0;

        return { value: valueJ0, valuePrev: valueJ1, valueAvg: (valueJ1 + valueJ2 + valueJ3) / 3 };
    };

    const totalMengePrev = artikelumsatzJ1.totalMenge;
    const totalMengeAvg = (artikelumsatzJ1.totalMenge + artikelumsatzJ2.totalMenge + artikelumsatzJ3.totalMenge) / 3;
    const totalAufwandPrev = artikelumsatzJ1.totalAufwand;
    const totalAufwandAvg =
        (artikelumsatzJ1.totalAufwand + artikelumsatzJ2.totalAufwand + artikelumsatzJ3.totalAufwand) / 3;
    const totalUmsatzPrev = artikelumsatzJ1.totalUmsatz;
    const totalUmsatzAvg =
        (artikelumsatzJ1.totalUmsatz + artikelumsatzJ2.totalUmsatz + artikelumsatzJ3.totalUmsatz) / 3;

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && (
                <table className={formStyles.reportFixedHeaderNoBgColor}>
                    <thead>{renderHeaderRow()}</thead>
                </table>
            )}
            <h1>Artikelumsatz</h1>
            {printView && (
                <table className={formStyles.reportFilterInfo}>
                    <tr>
                        <td>Druckdatum: </td>
                        <td>{moment().format('DD.MM.YYYY HH:mm:ss')}</td>
                    </tr>
                </table>
            )}
            {!datumVon || !datumBis || !artikelGruppeNr || !artikelNr ? (
                <div>Bitte wählen Sie einen Zeitraum und einen Artikel.</div>
            ) : (
                <table className={formStyles.reportTable}>
                    <thead>{renderHeaderRow()}</thead>
                    <tbody>
                        {(artikelumsatzJ0.lines || []).map((line) => {
                            const { value: menge, valuePrev: mengePrev, valueAvg: mengeAvg } = getValueWithCompares(
                                line.kNr,
                                'menge',
                            );

                            const {
                                value: aufwand,
                                valuePrev: aufwandPrev,
                                valueAvg: aufwandAvg,
                            } = getValueWithCompares(line.kNr, 'aufwand');

                            const { value: umsatz, valuePrev: umsatzPrev, valueAvg: umsatzAvg } = getValueWithCompares(
                                line.kNr,
                                'umsatz',
                            );

                            return (
                                <tr>
                                    <td
                                        className={classnames(
                                            styles.tdBody,
                                            printView
                                                ? formStyles.reportKundePrintCell
                                                : formStyles.reportKundeLimitedCell,
                                        )}
                                    >
                                        <OpenLink
                                            link={createRecordUrl(null, line.kNr)}
                                            label={line.kName + ' (' + line.kNr + ')'}
                                        />
                                    </td>
                                    <td
                                        className={classnames(
                                            styles.tdBody,
                                            printView
                                                ? formStyles.reportArtikelGruppePrintCell
                                                : formStyles.reportArtikelGruppeCell,
                                        )}
                                    >
                                        {artikelGruppenByNr[line.artikelGruppeNr]?.text || line.artikelGruppeNr}
                                    </td>
                                    <td
                                        className={classnames(
                                            styles.tdBody,
                                            printView
                                                ? formStyles.reportArtikelNrPrintCell
                                                : formStyles.reportArtikelNrCell,
                                        )}
                                    >
                                        {line.artikelNr}
                                    </td>
                                    <td
                                        className={classnames(
                                            styles.tdBody,
                                            printView
                                                ? formStyles.reportArtikelTextPrintCell
                                                : formStyles.reportArtikelTextCell,
                                        )}
                                    >
                                        {line.artikelText}
                                    </td>
                                    <td
                                        className={classnames(
                                            styles.tdBody,
                                            printView
                                                ? formStyles.reportReclayProduktgruppePrintCell
                                                : formStyles.reportReclayProduktgruppeCell,
                                        )}
                                    >
                                        {line.reclayProduktgruppe}
                                    </td>
                                    <td
                                        className={classnames(
                                            styles.tdBody,
                                            printView
                                                ? formStyles.reportEinheitPrintCell
                                                : formStyles.reportEinheitCell,
                                        )}
                                    >
                                        {einheitenByCode[line.einheit]?.text || line.einheit}
                                    </td>
                                    {renderValueWithGrowth(menge, mengePrev, mengeAvg, styles.menge, false)}
                                    {renderValueWithGrowth(aufwand, aufwandPrev, aufwandAvg, styles.aufwand, true)}
                                    {renderValueWithGrowth(umsatz, umsatzPrev, umsatzAvg, styles.umsatz, true)}
                                    {/* <td className={moneyClassNameBody}>{formatDecimal(line.bMarge, 2)}</td> */}
                                    {/* <td className={moneyClassNameBody}>{formatPercent(line.bMargeP, 2)}</td> */}
                                    {/* <td className={moneyClassNameBody}>{formatPercent(0, 2)}</td> */}
                                    {/* <td className={moneyClassNameBody}>{formatPercent(0, 2)}</td> */}
                                </tr>
                            );
                        })}
                        <tr>
                            <td colSpan={6} />
                            {renderValueWithGrowth(
                                artikelumsatzJ0.totalMenge,
                                totalMengePrev,
                                totalMengeAvg,
                                styles.menge,
                                false,
                            )}
                            {renderValueWithGrowth(
                                artikelumsatzJ0.totalAufwand,
                                totalAufwandPrev,
                                totalAufwandAvg,
                                styles.aufwand,
                                true,
                            )}
                            {renderValueWithGrowth(
                                artikelumsatzJ0.totalUmsatz,
                                totalUmsatzPrev,
                                totalUmsatzAvg,
                                styles.umsatz,
                                true,
                            )}
                        </tr>
                    </tbody>
                </table>
            )}
        </div>
    );
};
